import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../../components/SEO';
import {
  ArrowRight,
  Sparkles,
  Hotel,
  Palmtree,
  Ship,
  Waves,
  Sun,
  Heart,
  Share2,
  QrCode,
  Ticket,
  Zap,
  ScanFace,
  Star,
  Camera,
  Utensils,
  Plane,
  Instagram,
  MessageCircle,
  Music2,
  ChevronRight,
  Sailboat,
  Coffee,
  MapPin,
  Wine,
  Compass,
  DollarSign,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import StatsDashboard from '../../components/StatsDashboard';
import { hospitalityImages } from '../../mock';

/* Phone frame */
function PhoneFrame({ children }) {
  return (
    <div className="relative mx-auto w-[220px] md:w-[240px]">
      <div className="relative aspect-[9/19] rounded-[2.5rem] bg-gradient-to-b from-[#1a1a24] to-[#0a0a0f] p-2 border border-white/10 shadow-2xl shadow-black/70">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-30" />
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-[#0a0a0f]">{children}</div>
      </div>
      <div className="absolute -inset-6 -z-10 bg-cyan-500/12 blur-3xl rounded-full" />
    </div>
  );
}

/* Screen 1 : Resort selfie search */
function ScreenResort() {
  return (
    <>
      <img src={hospitalityImages.familyBeach} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/40" />

      <div className="absolute top-10 left-3 right-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white text-[10px] font-black shadow">
          🌴
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-white leading-tight">Playa del Carmen · Riviera Resort</div>
          <div className="text-[8px] text-white/70 leading-tight">Habitación 421 · Estadía 7 noches</div>
        </div>
      </div>

      <div className="absolute top-[34%] left-1/2 -translate-x-1/2 w-[52%] aspect-square">
        <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl-md" />
        <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr-md" />
        <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl-md" />
        <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white rounded-br-md" />
      </div>

      <div className="absolute bottom-6 left-3 right-3 text-center">
        <div className="text-[10px] font-semibold text-white mb-1">Encontrá las fotos de tu viaje</div>
        <div className="text-[8px] text-white/70 mb-2">Sacate una selfie · sin apps</div>
        <div className="w-11 h-11 rounded-full bg-white mx-auto" />
      </div>
    </>
  );
}

/* Screen 2 : Branded holiday album */
function ScreenResortAlbum() {
  return (
    <>
      <div className="absolute inset-0 bg-[#0a0a0f]" />

      <div className="absolute top-10 left-0 right-0 px-3">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white text-sm">🌴</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-white leading-tight">Familia López · Cancún</div>
            <div className="text-[8px] text-white/50 leading-tight">Álbum privado · 84 fotos</div>
          </div>
          <Heart className="w-4 h-4 text-rose-400 fill-rose-400" />
        </div>

        <div className="rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 p-1.5 flex items-center justify-between text-white">
          <div className="flex items-center gap-1.5">
            <Sun className="w-3 h-3" />
            <span className="text-[9px] font-bold">DÍA 4/7</span>
          </div>
          <div className="text-[7px] uppercase tracking-wider opacity-80">Riviera Resort</div>
          <div className="text-[9px] font-black">HD</div>
        </div>
      </div>

      <div className="absolute top-[36%] left-3 right-3">
        <div className="rounded-lg overflow-hidden aspect-[4/3] border border-white/8 relative">
          <img src={hospitalityImages.familyBeachSunset} alt="" className="w-full h-full object-cover" />
          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-white/95 text-slate-900 text-[7px] font-black italic">Riviera</div>
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[7px] font-black">SUNSET</div>
        </div>
      </div>

      <div className="absolute top-[63%] left-3 right-3">
        <div className="grid grid-cols-4 gap-1 mb-2">
          {[hospitalityImages.couplePool, hospitalityImages.breakfast, hospitalityImages.kayak, hospitalityImages.beachBar].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="text-[7px] uppercase tracking-wider text-white/40 mb-1">Partners del resort</div>
        <div className="flex items-center gap-1">
          {[
            { name: 'Aeromexico', bg: 'bg-blue-700' },
            { name: 'Bacardí', bg: 'bg-black border border-white/30' },
            { name: 'Spa', bg: 'bg-emerald-700' },
          ].map((s) => (
            <div key={s.name} className={`flex-1 h-5 rounded ${s.bg} flex items-center justify-center`}>
              <span className="text-[7px] font-bold text-white italic">{s.name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* Screen 3 : Share holiday */
function ScreenShareHoliday() {
  const contacts = [
    { name: 'Mamá', img: hospitalityImages.familyBeach, social: 'wa' },
    { name: 'Insta', img: hospitalityImages.couplePool, social: 'ig' },
    { name: 'Papá', img: hospitalityImages.kidsPool, social: 'wa' },
    { name: 'Nico', img: hospitalityImages.sunsetDeck, social: 'tt' },
  ];
  const iconOf = {
    wa: { Icon: MessageCircle, bg: 'bg-emerald-500' },
    ig: { Icon: Instagram, bg: 'bg-gradient-to-br from-fuchsia-500 to-orange-500' },
    tt: { Icon: Music2, bg: 'bg-black border border-white/40' },
  };
  return (
    <>
      <img src={hospitalityImages.familyBeachSunset} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/50" />

      <div className="absolute top-12 left-3 right-3 flex items-center justify-between">
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-400 text-slate-900 text-[9px] font-black">
          <Sun className="w-3 h-3" /> Vacaciones 2026
        </div>
        <div className="text-[8px] text-white/80">Riviera</div>
      </div>

      <div className="absolute left-3 right-3 top-[42%] rounded-2xl bg-white/95 backdrop-blur-md p-3 shadow-xl">
        <div className="text-[8px] uppercase tracking-wider text-black/50 mb-2 font-semibold">Compartir con</div>
        <div className="flex items-center justify-between gap-1">
          {contacts.map((c) => {
            const { Icon, bg } = iconOf[c.social];
            return (
              <div key={c.name} className="flex flex-col items-center gap-1">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-black/10">
                    <img src={c.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${bg} flex items-center justify-center shadow`}>
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                <div className="text-[8px] text-black font-medium">{c.name}</div>
              </div>
            );
          })}
          <div className="flex flex-col items-center gap-1">
            <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center">
              <ChevronRight className="w-4 h-4 text-black" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center gap-2">
        <span className="text-white text-[9px] font-black italic">#RivieraResort</span>
        <span className="text-white/60 text-[8px]">·</span>
        <span className="text-white text-[9px] font-black italic">#MisVacaciones</span>
      </div>
    </>
  );
}

/* Hotel Event Card */
function ResortEventCard() {
  return (
    <div className="relative w-full max-w-[440px] mx-auto">
      <div className="relative w-full aspect-[3/4] rounded-3xl overflow-hidden border border-white/10">
        <img src={hospitalityImages.familyBeach} alt="familia en la playa" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

        <div className="absolute top-6 left-1/2 -translate-x-1/2 w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-display font-black text-2xl shadow-2xl shadow-cyan-500/40 border-4 border-[#0a0a0f]">
          m
        </div>

        <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-[#0a0a0f]/85 backdrop-blur-md border border-white/10 overflow-hidden">
          <div className="relative h-24 overflow-hidden bg-[#111116]">
            <img src={hospitalityImages.poolResort} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-transparent to-[#0a0a0f]" />
            <div className="relative h-full flex items-center justify-center gap-6 px-4">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-cyan-500 flex items-center justify-center text-white shadow-lg">
                  <Palmtree className="w-6 h-6" />
                </div>
                <div className="text-[9px] font-black text-cyan-300 mt-1 tracking-wider">RESORT</div>
              </div>
              <div className="text-white/70 font-display font-bold text-lg">·</div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 shadow-lg">
                  <Ship className="w-6 h-6" />
                </div>
                <div className="text-[9px] font-black text-white mt-1 tracking-wider">CRUISE</div>
              </div>
            </div>
          </div>

          <div className="p-4 pt-3">
            <div className="text-sm font-display font-bold text-white leading-tight">Riviera Beach Resort & Spa</div>
            <div className="text-xs text-white/60 mt-0.5">Playa del Carmen · 5★ · 342 habitaciones</div>
            <div className="text-xs text-white/40 mt-1">Temporada 2026 · 100% ocupación</div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
              <div className="text-[11px] text-cyan-300 font-semibold flex items-center gap-1">
                <Camera className="w-3 h-3" /> +18 mil fotos por semana
              </div>
              <div className="px-2.5 py-1 rounded-full bg-white text-slate-900 text-[10px] font-bold flex items-center gap-1">
                Ver álbum <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Hospitality gallery — many moments one holiday */
function HospitalityGallery() {
  const { t } = useTranslation();
  const names = t('services.hospitality.gallery.items', { returnObjects: true });
  const srcs = [
    hospitalityImages.familyBeach,
    hospitalityImages.couplePool,
    hospitalityImages.spaWellness,
    hospitalityImages.restaurantDinner,
    hospitalityImages.excursion,
    hospitalityImages.cruiseShip,
    hospitalityImages.beachBar,
    hospitalityImages.yoga,
    hospitalityImages.sunsetDeck,
  ];
  const items = srcs.map((src, i) => ({ src, name: names[i] }));
  return (
    <div className="overflow-hidden">
      <div className="flex gap-3 marquee-track">
        {[...items, ...items].map((s, i) => (
          <div key={i} className="relative w-[240px] h-[300px] shrink-0 rounded-2xl overflow-hidden border border-white/10">
            <img src={s.src} alt={s.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 text-white font-display text-xl font-bold">{s.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Hospitality sponsor tiers — travel brands + hotel services combined */
function HospitalitySponsorTiers() {
  const { t } = useTranslation();
  const tiers = [
    { name: t('services.hospitality.partners.tiers.premium'), badge: 'from-slate-100 to-slate-300 text-slate-900', logos: ['AEROMEXICO'], size: 'h-16' },
    { name: t('services.hospitality.partners.tiers.partner'), badge: 'from-cyan-300 to-teal-400 text-cyan-950', logos: ['Bacardí', 'Sunwing', 'Amex Travel'], size: 'h-12' },
    { name: t('services.hospitality.partners.tiers.services'), badge: 'from-amber-300 to-orange-400 text-amber-950', logos: ['Spa Wellness', 'La Terraza', 'Diving Center'], size: 'h-10' },
    { name: t('services.hospitality.partners.tiers.local'), badge: 'from-emerald-400 to-teal-500 text-emerald-950', logos: ['Xcaret', 'Isla Mujeres Tour', 'Tequila Route', 'Cenote Ride'], size: 'h-8' },
  ];
  return (
    <div className="space-y-4">
      {tiers.map((t) => (
        <div key={t.name} className="flex items-center gap-4">
          <div className="w-24 shrink-0 text-right">
            <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${t.badge} text-[10px] font-black uppercase tracking-wider`}>{t.name}</div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {t.logos.map((l) => (
              <div key={l} className={`${t.size} px-4 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center min-w-[110px]`}>
                <span className="text-white/85 font-display font-bold text-sm">{l}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Hospitality email templates */
function HospitalityEmailMockup() {
  return (
    <div className="relative">
      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[300px] relative z-10">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/8">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white">
            <Palmtree className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">Riviera Resort · Tus vacaciones</div>
            <div className="text-[8px] text-black/50">Para: familia.lopez@mail.com</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">Familia López, gracias por elegirnos 🌴</div>
        <div className="w-full aspect-[16/10] rounded-lg overflow-hidden mb-2">
          <img src={hospitalityImages.familyBeach} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 p-2.5 text-white">
          <div className="text-[8px] uppercase tracking-wider opacity-80">Regalo del resort</div>
          <div className="text-sm font-black leading-tight">Cena romántica gratis para su próxima estadía</div>
          <div className="text-[9px] mt-1 opacity-90">Código: RIVIERA-LOVE · vence 30/12</div>
        </div>
      </div>

      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[280px] absolute top-16 right-0 rotate-3 z-20">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-black/8">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white">
            <Plane className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">Aeromexico · Recuerdo del vuelo</div>
            <div className="text-[8px] text-black/50">Para: familia.lopez@mail.com</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">¿Ya soñás con el próximo destino?</div>
        <div className="text-[9px] text-black/70 leading-relaxed mb-2">
          Como huésped del <span className="font-bold text-cyan-600">Riviera Resort</span>, tenés 15% off en vuelos a Cancún hasta 03/2027.
        </div>
        <div className="grid grid-cols-3 gap-1 mb-2">
          {[hospitalityImages.familyBeach, hospitalityImages.sunsetDeck, hospitalityImages.couplePool].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="h-6 rounded bg-cyan-500 flex items-center justify-center text-white text-[9px] font-black">
          Ver próximos vuelos
        </div>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-2xl p-4 max-w-[260px] absolute bottom-0 left-8 -rotate-2 z-0">
        <div className="text-[9px] uppercase tracking-wider opacity-80 mb-1">🍷 Spa & restaurantes</div>
        <div className="text-sm font-black leading-tight mb-2">Reservá tu día en el spa</div>
        <div className="text-[9px] opacity-90 leading-relaxed mb-3">
          Los huéspedes que disfrutaron el spa este mes recibieron un 20% off en su próximo tratamiento.
        </div>
        <div className="h-6 rounded bg-white text-orange-700 flex items-center justify-center text-[9px] font-bold">
          Reservar ahora
        </div>
      </div>
    </div>
  );
}

/* Guest journey — timeline */
function GuestJourney() {
  const { t } = useTranslation();
  const stagesData = t('services.hospitality.guestJourney.stages', { returnObjects: true });
  const icons = [Hotel, Sun, Compass, Wine, Waves, Heart];
  const stages = stagesData.map((s, i) => ({ ...s, icon: icons[i] }));
  return (
    <div className="relative">
      <div className="absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-cyan-500/40 via-teal-500/40 to-amber-500/40" />
      <div className="space-y-6">
        {stages.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.day} className="relative flex items-start gap-5">
              <div className="relative shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-teal-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-200 backdrop-blur">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 pt-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300 mb-1">{s.day}</div>
                <div className="font-display text-lg font-semibold text-white leading-tight">{s.title}</div>
                <div className="text-sm text-white/60 mt-1 leading-relaxed">{s.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const propertyKindIcons = [Palmtree, Ship, Hotel, Sailboat, Sun, Compass];

export default function Hospitality() {
  const { t } = useTranslation();
  const propertyKindsData = t('services.hospitality.propertyTypes.items', { returnObjects: true });
  const propertyKinds = propertyKindsData.map((p, i) => ({ ...p, icon: propertyKindIcons[i] }));
  const howItWorksSteps = t('services.hospitality.howItWorks.steps', { returnObjects: true });
  const howItWorksImages = [
    'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/fgcqlucf_MOCKUP%20FAMILIA%20%281%29.webp',
    'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/1egkpwic_MOCKUP%20FAMILIA%20%282%29.webp',
    'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/znhux9hk_MOCKUP%20FAMILIA%20%283%29.webp',
  ];
  const howItWorksColors = ['bg-cyan-500', 'bg-teal-500', 'bg-amber-500'];
  const partnersBullets = t('services.hospitality.partners.bullets', { returnObjects: true });
  const partnersBulletIcons = [Plane, Utensils, Ticket, QrCode];
  const emailsItems = t('services.hospitality.emails.items', { returnObjects: true });
  const emailsIcons = [Heart, Ticket, Utensils, Plane, Star];
  const benefitsItems = t('services.hospitality.benefits.items', { returnObjects: true });
  const benefitsIcons = [DollarSign, TrendingUp, Heart];
  const ambassadorsBullets = t('services.hospitality.ambassadors.bullets', { returnObjects: true });
  const measureTags = t('services.hospitality.measure.tags', { returnObjects: true });
  const impactItems = t('services.hospitality.impact.items', { returnObjects: true });
  return (
    <>
      <SEO
        title={t('services.hospitality.seoTitle')}
        description={t('services.hospitality.seoDescription')}
        path="/servicios/hospitality"
        schema={serviceSchema({ name: 'Fotos con IA para Hospitality y Hoteles', description: 'Reconocimiento facial y álbumes personalizados para hoteles, resorts, cruceros y experiencias turísticas.', path: '/servicios/hospitality' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-32 w-[500px] h-[500px] bg-cyan-500/14 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-amber-500/12 rounded-full blur-[160px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-300 mb-5 hover:text-cyan-200 transition-colors">
                <span>{t('services.hospitality.breadcrumb')}</span><span>/</span><span className="text-white/60">{t('services.hospitality.breadcrumbCurrent')}</span>
              </Link>
              <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
                {t('services.hospitality.heroTitle1')}
                <span className="block bg-gradient-to-r from-cyan-300 via-teal-300 to-amber-300 bg-clip-text text-transparent">
                  {t('services.hospitality.heroTitleHighlight')}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                {t('services.hospitality.heroSubtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/request-demo">
                  <Button data-testid="hospitality-request-demo-btn" className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                    {t('services.hospitality.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/precios">
                  <Button data-testid="hospitality-pricing-btn" variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.hospitality.ctaPricing')}
                  </Button>
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                <div>
                  <div className="font-display text-2xl font-bold text-white">$18</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.hospitality.heroStats.ancillary')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">+38%</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.hospitality.heroStats.satisfaction')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">12</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.hospitality.heroStats.languages')}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <ResortEventCard />
            </div>
          </div>
        </div>
      </section>

      {/* MANY MOMENTS ONE HOLIDAY */}
      <section className="py-16 lg:py-20 bg-[#08080c] border-y border-white/5">
        <div className="container-mi mb-8 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">{t('services.hospitality.gallery.eyebrow')}</div>
          <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight">
            {t('services.hospitality.gallery.titleLine1')} <span className="bg-gradient-to-r from-cyan-300 to-amber-300 bg-clip-text text-transparent">{t('services.hospitality.gallery.titleHighlight')}</span>
          </h2>
        </div>
        <HospitalityGallery />
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">{t('services.hospitality.howItWorks.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.hospitality.howItWorks.title')}
              <br />
              {t('services.hospitality.howItWorks.titleLine2')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {howItWorksSteps.map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="relative w-full max-w-[280px]">
                  <img
                    src={howItWorksImages[i]}
                    alt={s.title}
                    className="w-full h-auto object-contain drop-shadow-[0_25px_50px_rgba(139,92,246,0.28)]"
                    loading="lazy"
                  />
                </div>
                <div className={`mt-6 w-8 h-8 rounded-full ${howItWorksColors[i]} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                  {i + 1}
                </div>
                <h3 className="font-display text-lg md:text-xl font-semibold mt-4 mb-2">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed max-w-xs">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUEST JOURNEY */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> {t('services.hospitality.guestJourney.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.hospitality.guestJourney.titleLine1')}
                <br />
                <span className="bg-gradient-to-r from-cyan-300 to-amber-300 bg-clip-text text-transparent">{t('services.hospitality.guestJourney.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.hospitality.guestJourney.description')}
              </p>
              <div className="mt-8 grid grid-cols-3 gap-4">
                <div className="rounded-xl bg-[#111116] border border-white/8 p-4">
                  <div className="font-display text-2xl font-bold text-white">2.4×</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.hospitality.guestJourney.stats.reviews')}</div>
                </div>
                <div className="rounded-xl bg-[#111116] border border-white/8 p-4">
                  <div className="font-display text-2xl font-bold text-white">64%</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.hospitality.guestJourney.stats.repeat')}</div>
                </div>
                <div className="rounded-xl bg-[#111116] border border-white/8 p-4">
                  <div className="font-display text-2xl font-bold text-white">18k</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.hospitality.guestJourney.stats.photos')}</div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-[#0d0d13] border border-white/8 p-6 md:p-8">
              <GuestJourney />
            </div>
          </div>
        </div>
      </section>

      {/* SPONSOR / PARTNER ECOSYSTEM */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> {t('services.hospitality.partners.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.hospitality.partners.titleLine1')}
                <br />
                <span className="bg-gradient-to-r from-cyan-300 to-amber-300 bg-clip-text text-transparent">{t('services.hospitality.partners.titleHighlight')}</span>
                <br />
                {t('services.hospitality.partners.titleLine3')}
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.hospitality.partners.description')}
              </p>
              <ul className="mt-8 space-y-3">
                {partnersBullets.map((text, i) => {
                  const Icon = partnersBulletIcons[i];
                  return (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-400/25 flex items-center justify-center text-cyan-300 shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm leading-relaxed pt-1">{text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-3xl bg-[#0d0d13] border border-white/8 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="text-sm font-semibold text-white">{t('services.hospitality.partners.dashboard.title')}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">{t('services.hospitality.partners.dashboard.meta')}</div>
              </div>
              <HospitalitySponsorTiers />
              <div className="mt-6 pt-6 border-t border-white/8 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-display text-xl font-bold text-white">4.2M</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.hospitality.partners.dashboard.impressions')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-white">186k</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.hospitality.partners.dashboard.clicks')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-cyan-300">6.1× ROI</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.hospitality.partners.dashboard.roi')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EMAILS A TU MEDIDA */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 -translate-y-1/2 -left-40 w-[500px] h-[500px] bg-cyan-500/6 rounded-full blur-[140px]" />
          <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[500px] h-[500px] bg-amber-500/6 rounded-full blur-[140px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3 flex items-center gap-2">
                <ScanFace className="w-3.5 h-3.5" /> {t('services.hospitality.emails.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.hospitality.emails.titleLine1')}
                <br />
                {t('services.hospitality.emails.titleLine2Prefix')} <span className="bg-gradient-to-r from-cyan-300 to-amber-300 bg-clip-text text-transparent">{t('services.hospitality.emails.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.hospitality.emails.description')}
              </p>
              <div className="mt-8 space-y-3">
                {emailsItems.map((item, i) => {
                  const Icon = emailsIcons[i];
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#111116] border border-white/8">
                      <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-400/25 flex items-center justify-center text-cyan-300 shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">{item.title}</div>
                        <div className="text-xs text-white/60 mt-0.5">{item.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative h-[520px]">
              <HospitalityEmailMockup />
            </div>
          </div>
        </div>
      </section>

      {/* PROPERTY KINDS */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">{t('services.hospitality.propertyTypes.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.hospitality.propertyTypes.title')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {propertyKinds.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.name} className="card-lift p-6 rounded-2xl bg-[#111116] border border-white/8">
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-400/25 flex items-center justify-center text-cyan-300 mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display text-lg font-semibold text-white">{s.name}</div>
                  <div className="text-sm text-white/60 mt-1">{s.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY IT WORKS — 3 pillars: revenue + upsell + engagement */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">{t('services.hospitality.benefits.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.hospitality.benefits.titleLine1')}
              <br />
              <span className="bg-gradient-to-r from-cyan-300 via-teal-300 to-amber-300 bg-clip-text text-transparent">{t('services.hospitality.benefits.titleHighlight')}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {benefitsItems.map((item, i) => {
              const Icon = benefitsIcons[i];
              return (
                <div key={i} className="rounded-2xl bg-[#111116] border border-white/8 p-6 flex flex-col">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white shadow-lg mb-5">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display text-xl font-semibold text-white mb-2">{item.title}</div>
                  <div className="text-sm text-white/65 leading-relaxed flex-1">{item.desc}</div>
                  <div className="mt-5 inline-flex self-start items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-400/25 text-cyan-200 text-[11px] font-semibold">
                    {item.pill}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* UGC / SOCIAL AMBASSADORS */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-32 w-[500px] h-[500px] bg-fuchsia-500/8 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] bg-cyan-500/8 rounded-full blur-[140px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3 flex items-center gap-2">
                <Instagram className="w-3.5 h-3.5" /> {t('services.hospitality.ambassadors.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.hospitality.ambassadors.titleLine1')}
                <br />
                <span className="bg-gradient-to-r from-fuchsia-300 via-pink-300 to-amber-300 bg-clip-text text-transparent">{t('services.hospitality.ambassadors.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.hospitality.ambassadors.description')}
              </p>
              <div className="mt-8 grid grid-cols-3 gap-3 max-w-lg">
                <div className="rounded-xl bg-[#111116] border border-white/8 p-4">
                  <div className="font-display text-2xl font-bold text-white">8/10</div>
                  <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1 leading-snug">{t('services.hospitality.ambassadors.stats.trust')}</div>
                </div>
                <div className="rounded-xl bg-[#111116] border border-white/8 p-4">
                  <div className="font-display text-2xl font-bold text-white">3.6×</div>
                  <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1 leading-snug">{t('services.hospitality.ambassadors.stats.mentions')}</div>
                </div>
                <div className="rounded-xl bg-[#111116] border border-white/8 p-4">
                  <div className="font-display text-2xl font-bold text-white">$0</div>
                  <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1 leading-snug">{t('services.hospitality.ambassadors.stats.cost')}</div>
                </div>
              </div>
              <ul className="mt-8 space-y-3">
                {ambassadorsBullets.map((text) => (
                  <li key={text} className="flex items-center gap-3 text-white/80">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shrink-0">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Instagram-style stack */}
            <div className="relative h-[520px]">
              {/* Card 1 */}
              <div className="absolute top-0 left-0 w-[280px] rounded-2xl bg-white shadow-2xl overflow-hidden rotate-[-4deg]">
                <div className="flex items-center gap-2 p-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-500 p-0.5">
                    <div className="w-full h-full rounded-full overflow-hidden">
                      <img src={hospitalityImages.familyBeach} alt="" className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-semibold text-black leading-tight">familia.lopez</div>
                    <div className="text-[9px] text-black/50">Riviera Beach Resort</div>
                  </div>
                  <div className="text-black/40 text-[10px]">•••</div>
                </div>
                <div className="aspect-square overflow-hidden">
                  <img src={hospitalityImages.familyBeach} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-3 mb-2 text-black">
                    <Heart className="w-4 h-4" />
                    <MessageCircle className="w-4 h-4" />
                    <Share2 className="w-4 h-4" />
                  </div>
                  <div className="text-[10px] text-black">
                    <span className="font-semibold">3.842 me gusta</span>
                  </div>
                  <div className="text-[10px] text-black/80 mt-1">
                    <span className="font-semibold">familia.lopez</span> El mejor viaje de nuestras vidas 💛 #RivieraResort #PlayaDelCarmen
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="absolute top-16 right-0 w-[260px] rounded-2xl bg-white shadow-2xl overflow-hidden rotate-[5deg]">
                <div className="flex items-center gap-2 p-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-500 p-0.5">
                    <div className="w-full h-full rounded-full overflow-hidden bg-slate-300">
                      <img src={hospitalityImages.couplePool} alt="" className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-semibold text-black leading-tight">sofi.travels</div>
                    <div className="text-[9px] text-black/50">Riviera Beach Resort</div>
                  </div>
                  <div className="text-black/40 text-[10px]">•••</div>
                </div>
                <div className="aspect-square overflow-hidden">
                  <img src={hospitalityImages.couplePool} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-3 mb-2 text-black">
                    <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                    <MessageCircle className="w-4 h-4" />
                    <Share2 className="w-4 h-4" />
                  </div>
                  <div className="text-[10px] text-black">
                    <span className="font-semibold">1.284 me gusta</span>
                  </div>
                  <div className="text-[10px] text-black/80 mt-1">
                    <span className="font-semibold">sofi.travels</span> Aniversario perfecto 🥂✨ @rivieraresort @aeromexico
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="absolute bottom-0 left-8 w-[240px] rounded-2xl bg-white shadow-2xl overflow-hidden rotate-[-2deg]">
                <div className="flex items-center gap-2 p-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-500 p-0.5">
                    <div className="w-full h-full rounded-full overflow-hidden bg-slate-300">
                      <img src={hospitalityImages.sunsetDeck} alt="" className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-semibold text-black leading-tight">mateo.explora</div>
                    <div className="text-[9px] text-black/50">Riviera Beach Resort</div>
                  </div>
                  <div className="text-black/40 text-[10px]">•••</div>
                </div>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={hospitalityImages.sunsetDeck} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-3 mb-2 text-black">
                    <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                    <MessageCircle className="w-4 h-4" />
                    <Share2 className="w-4 h-4" />
                  </div>
                  <div className="text-[10px] text-black/80">
                    <span className="font-semibold">846 me gusta</span> · <span className="font-semibold">mateo.explora</span> Este sunset no se olvida 🌅
                  </div>
                </div>
              </div>

              {/* Floating stat badge */}
              <div className="absolute top-1/2 right-4 -translate-y-1/2 px-3 py-2 rounded-2xl bg-[#0a0a0f]/90 backdrop-blur-md border border-cyan-400/30 shadow-xl">
                <div className="text-[9px] uppercase tracking-wider text-cyan-300 font-semibold">{t('services.hospitality.ambassadors.reach.label')}</div>
                <div className="font-display text-xl font-bold text-white leading-none mt-1">2.4M</div>
                <div className="text-[9px] text-white/50 mt-0.5">{t('services.hospitality.ambassadors.reach.period')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEASURE SUCCESS — Statistics dashboard */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/18 via-teal-500/10 to-amber-500/15 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-cyan-500/18 rounded-full blur-[160px]" />
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> {t('services.hospitality.measure.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white">
                {t('services.hospitality.measure.titleLine1')}
                <br />
                {t('services.hospitality.measure.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
                {t('services.hospitality.measure.description')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {measureTags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-sm text-white">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <StatsDashboard
                config={{
                  accent: {
                    chartFrom: 'from-cyan-400',
                    chartVia: 'via-teal-400',
                    chartTo: 'to-amber-400',
                    dayPillBg: 'bg-cyan-500',
                  },
                  browserUrl: 'dashboard.momentia.shop / Riviera Beach Resort',
                  period: t('services.hospitality.measure.dashboard.period'),
                  kpis: [
                    { icon: '🌴', label: t('services.hospitality.measure.dashboard.kpiActive'), value: '428', sub: t('services.hospitality.measure.dashboard.kpiActiveSub'), color: 'text-cyan-600' },
                    { icon: '📸', label: t('services.hospitality.measure.dashboard.kpiPhotos'), value: '24', sub: t('services.hospitality.measure.dashboard.kpiPhotosSub'), color: 'text-teal-600' },
                    { icon: '🔥', label: t('services.hospitality.measure.dashboard.kpiEngagement'), value: '38%', sub: t('services.hospitality.measure.dashboard.kpiEngagementSub'), color: 'text-amber-600' },
                  ],
                  chart: {
                    title: t('services.hospitality.measure.dashboard.chartTitle'),
                    bars: [50, 75, 60, 85, 90, 70, 95, 88, 78, 92, 65, 96, 88],
                  },
                  sponsorRow: {
                    title: t('services.hospitality.measure.dashboard.revenueTitle'),
                    subtitle: t('services.hospitality.measure.dashboard.revenueSubtitle'),
                    items: [
                      { icon: '💆', label: t('services.hospitality.measure.dashboard.revenueSpa'), value: '$2.4k' },
                      { icon: '🌊', label: t('services.hospitality.measure.dashboard.revenueExcursions'), value: '$1.8k' },
                      { icon: '🍷', label: t('services.hospitality.measure.dashboard.revenueFB'), value: '$1.6k' },
                    ],
                  },
                  badges: [
                    { emoji: '😊', text: t('services.hospitality.measure.dashboard.badgeGuests'), pos: 'tl' },
                    { emoji: '🔥', text: t('services.hospitality.measure.dashboard.badgeShares'), pos: 'br' },
                    { emoji: '💰', text: t('services.hospitality.measure.dashboard.badgeRevenue'), pos: 'bml' },
                  ],
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT METRICS */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/15 via-teal-500/10 to-amber-500/15 pointer-events-none" />
        <div className="container-mi relative">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3">{t('services.hospitality.impact.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white leading-tight">
              {t('services.hospitality.impact.titleLine1')}
              <br />
              {t('services.hospitality.impact.titleLine2')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {impactItems.map((m) => (
              <div key={m.label} className="rounded-2xl bg-white/[0.08] backdrop-blur-md border border-white/20 p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-white">{m.value}</div>
                <div className="text-sm text-white/70 mt-2 leading-snug">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#0e2a2c] via-[#101018] to-[#1a1410] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-amber-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-cyan-400/25 bg-cyan-500/10 text-xs text-cyan-200 mb-6">
                <Zap className="w-3 h-3" /> {t('services.hospitality.finalCta.badge')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white">
                {t('services.hospitality.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('services.hospitality.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to="/request-demo">
                  <Button data-testid="hospitality-cta-demo-btn" className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    {t('services.hospitality.finalCta.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/precios">
                  <Button data-testid="hospitality-cta-pricing-btn" variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.hospitality.finalCta.ctaPricing')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
